import { IColumnToolPanel } from 'ag-grid-community';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { AgGridConfigurationService } from '@app/shared/services/ag-grid-configuration.service';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import { TableLazyLoadEvent } from 'primeng/table';
import { ActivatedRoute } from '@angular/router';
import { ReleaseMgmtService } from '@app/shared/services/releaseMgmtService';
import { LoaderService } from '@app/shared/services/loader.service';
import { MatIconModule } from '@angular/material/icon';
import { PageHeaderComponent } from '@app/shared/page-header/page-header.component';
import { CommonModule } from '@angular/common';
import { ICellRendererParams } from 'ag-grid-community';
import moment from 'moment';

@Component({
    standalone: true,
    selector: 'app-release-manifest-status',
    templateUrl: './release-manifest-status.component.html',
    styleUrls: ['./release-manifest-status.component.scss'],
    imports: [AgGridModule, MatIconModule, PageHeaderComponent, CommonModule]
})
export class ReleaseManifestStatusComponent implements OnInit {
    @Input() status: string;
    @Input() tooltip: string;
    @Input() color: string;
    @Input() state: string;
    @ViewChild('agGrid') agGrid: AgGridAngular;

    paginationPageSize = 10;
    infiniteInitialRowCount = 30;
    quickFilterValue = '';
    overlayNoRowsTemplate = `<span style="font-weight: 400; color: #757575; ">No records found</span>`;
    agGridColDef: any;
    columnDefs = [];
    defaultColDef = {
        minWidth: 0,
        with: 0,
        resizable: true,
        sortable: true,
        filter: true,
    };
    // `gridDataParams` is used to store grid parameters
    gridDataParams: any;
    // `api` is used to interact with the grid
    api: any;
    // `gridColumnApi` is used to interact with the grid's columns
    public gridColumnApi: any;
    public columnToolPanel: IColumnToolPanel;
    releaseId = 0;
    releaseName = '';
    isRefreshing = false;
    pageTitle = 'Release Manifest Status';
    pageSubTitle = 'View the status of the release manifest.';
    constructor(
        public readonly agGridConfigurationService: AgGridConfigurationService,
        public route: ActivatedRoute,
        private readonly releaseMgmtService: ReleaseMgmtService,
        public loader: LoaderService
    ) {

    }

    ngOnInit(): void {
        this.agGridColDef = {
            tableName: 'releaseManifestStatus',
            column: [
                {
                    headerName: 'Release Manifest Status', columnName: 'pipelineStatus', field: 'pipelineStatus', dataType: 'string', columnType: 'string', cellStyle: 'left', filter: true, sortable: true, editable: false, width: this.columnWidth('gridManifest', 20), minWidth: this.columnWidth('gridManifest', 15), maxWidth: this.columnWidth('gridManifest', 25),
                    tooltipField: 'pipelineStatus'
                },
                {
                    headerName: 'Triggered On',
                    dataType: 'date',
                    field: 'triggerDateTime',
                    columnName: 'triggerDateTime',
                    filter: true,
                    width: this.columnWidth('gridManifest', 20),
                    minWidth: this.columnWidth('gridManifest', 15),
                    maxWidth: this.columnWidth('gridManifest', 25),
                    tooltipValueGetter: (params: ICellRendererParams) => {
                        if (!params.value) return '';
                        const date = moment(params.value);
                        return date.isValid() ? date.format('MMM DD, YYYY, hh:mm A') : '';
                    },
                    cellRenderer: (params: ICellRendererParams) => {
                        if (!params.value) return '';
                        const date = moment(params.value);
                        return date.isValid() ? date.format('MMM DD, YYYY, hh:mm A') : '';
                    }
                    },
                { headerName: 'Triggered By', columnName: 'triggeredBy', field: 'triggeredBy', dataType: 'string', columnType: 'string', cellStyle: 'left', filter: true, sortable: true, editable: false, width: this.columnWidth('gridManifest', 20), minWidth: this.columnWidth('gridManifest', 15), maxWidth: this.columnWidth('gridManifest', 25), tooltipField: 'triggeredBy' },
                { headerName: 'Status Log', columnName: 'failureComments', field: 'failureComments', dataType: 'string', columnType: 'string', cellStyle: 'left', filter: true, sortable: true, editable: false, flex: 1, width: this.columnWidth('gridManifest', 20), minWidth: this.columnWidth('gridManifest', 15), maxWidth: this.columnWidth('gridManifest', 25), tooltipField: 'failureComments' },
            ]
        };
        this.columnDefs = this.agGridConfigurationService.GetReleaseManifestStatusColDefs(this.agGridColDef);

        this.releaseId = Number(this.route.snapshot.paramMap.get('releaseId'))
    }


    columnWidth(id, percentage) {
        const div = document.getElementById(id);  // HTML ID of Grid
        const parent = div.parentElement;
        const width = parent.clientWidth * percentage / 100;  // Pixel calculation
        return width;
    }
    // Set the initial page size for pagination
    pageSize = 10;
    onGridReady(params: any) {
        this.agGrid.api = params.api;
        // columnApi merged into api in AG Grid v31

        this.gridDataParams = params;
        this.api = params.api;
        this.gridColumnApi = params.api;
        this.api.paginationSetPageSize(this.pageSize);
        const datasource = this.getGridData(null);
        this.api.setServerSideDatasource(datasource);
    }

    onQuickFilterChanged(event: any) {
        this.quickFilterValue = event.target.value;
        if (this.agGrid && this.agGrid.api) {
            this.agGrid.api.setQuickFilter(this.quickFilterValue);
            // Force overlay check
            if (this.agGrid.api.getDisplayedRowCount() === 0) {
                this.agGrid.api.showNoRowsOverlay();
            } else {
                this.agGrid.api.hideOverlay();
            }
        }
    }

    clearQuickFilter() {
        this.quickFilterValue = '';
        if (this.agGrid && this.agGrid.api && typeof this.agGrid.api.setQuickFilter === 'function') {
            this.agGrid.api.setQuickFilter('');
        }
    }

    refreshTable() {
        this.isRefreshing = true;
        // `datasource` is set to the result of the `getGridData` method with null as the argument
        const datasource = this.getGridData(null);
        // The server-side datasource for the ag-Grid is set to `datasource`
        this.api.setServerSideDatasource(datasource);
    }

    /**
       * Retrieves the grid data for lazy loading.
       * @param event The lazy load event containing the request parameters.
       * @returns An object with a `getRows` method that sets the column definitions for the ag-Grid,
       * makes a request to get the release details list, and sets the row data for the ag-Grid.
       */
    /* istanbul ignore next */
    getGridData(event: TableLazyLoadEvent) {
        return {
            // `getRows` is a method that sets the column definitions for the ag-Grid, makes a request to get the release details list, and sets the row data for the ag-Grid
            getRows: (params) => {
                // The column definitions for the ag-Grid are set to `columnDefs`
                this.api.setColumnDefs(this.columnDefs);
                this.loader.showSpinner.next(true);
                this.isRefreshing = true;

                this.releaseMgmtService.apiGetReleaseManifestStatus(this.releaseId).subscribe({
                    next: (data) => {
                        const filterModel = params.request.filterModel;
                        let filteredData = data;
                        if (filterModel && filterModel.triggerDateTime && filterModel.triggerDateTime.dateFrom) {
                        const filterDate = new Date(filterModel.triggerDateTime.dateFrom);
                        filteredData = data.filter(item => {
                            const cellDate = new Date(item.triggerDateTime);
                            return (
                            cellDate.getFullYear() === filterDate.getFullYear() &&
                            cellDate.getMonth() === filterDate.getMonth() &&
                            cellDate.getDate() === filterDate.getDate()
                            );
                        });
                        }
                        // data is expected to be an array of ReleaseManifestStatusDto
                        const rowData = Array.isArray(filteredData) ? filteredData.map(item => ({
                            releaseId: item.releaseId,
                            releaseName: item.releaseName,
                            pipelineStatus: item.pipelineStatus,
                            triggerDateTime: item.triggerDateTime,
                            triggeredBy: item.triggeredBy,
                            failureComments: item.failureComments
                        })) : [];
                        // Set releaseName from the first item if available
                        if (rowData.length > 0 && rowData[0].releaseName) {
                            this.releaseName = rowData[0].releaseName;
                        }
                        params.success({
                            rowData: rowData,
                            rowCount: rowData.length,
                        });
                        if (rowData.length === 0) {
                            this.api.showNoRowsOverlay();
                        } else {
                            this.api.hideOverlay();
                        }
                        this.loader.showSpinner.next(false);
                        this.isRefreshing = false;
                    },
                    error: (error) => {
                        this.overlayNoRowsTemplate = `<span style="font-weight: 400; color: #757575;">No records found</span>`;
                        params.success({
                            rowData: [],
                            rowCount: 0,
                        });
                        this.api.showNoRowsOverlay();
                        this.loader.showSpinner.next(false);
                        this.isRefreshing = false;
                    }
                });
            }
        };
    }

}
